import { create } from 'zustand';

// Types
export type CrudStatus = 'idle' | 'success' | 'error';

export interface IFilter {
  [key: string]: any;
}

export interface TableState {
  page: number;
  limit: number;
  totalItems: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setTotalItems: (total: number) => void;
}

export interface CrudState {
  // Loading states
  loadingList: boolean;
  loadingAction: boolean;

  // Status
  status: CrudStatus;
  errorMessage: string;
  feedback: boolean;

  // Filter
  filter: IFilter;

  // Modal states
  openAddCrudModal: boolean;
  openEditCrudModal: boolean;
  openDeleteCrudModal: boolean;
  openViewCrudModal: boolean;

  // Selected item
  selectedItem: any | null;

  // Table state
  tableState: TableState;

  // Actions
  setLoadingList: (loading: boolean) => void;
  setLoadingAction: (loading: boolean) => void;
  setStatus: (status: CrudStatus) => void;
  setErrorMessage: (message: string) => void;
  setFeedback: (feedback: boolean) => void;
  setFilter: (filter: IFilter) => void;
  setOpenAddCrudModal: (open: boolean) => void;
  setOpenEditCrudModal: (open: boolean, item?: any) => void;
  setOpenDeleteCrudModal: (open: boolean, item?: any) => void;
  setOpenViewCrudModal: (open: boolean, item?: any) => void;
  setSelectedItem: (item: any | null) => void;
  closeCrudModal: () => void;
  resetCrud: () => void;
}

// Initial state
const initialState = {
  loadingList: false,
  loadingAction: false,
  status: 'idle' as CrudStatus,
  errorMessage: '',
  feedback: false,
  filter: {},
  openAddCrudModal: false,
  openEditCrudModal: false,
  openDeleteCrudModal: false,
  openViewCrudModal: false,
  selectedItem: null,
};

/**
 * Zustand store para gerenciar estado CRUD
 * Substitui o CrudContext do devyx-ui mantendo API similar
 */
export const useCrudStore = create<CrudState>(set => ({
  ...initialState,

  // Table state separado para facilitar uso
  tableState: {
    page: 1,
    limit: 10,
    totalItems: 0,
    setPage: (page: number) =>
      set(state => ({
        tableState: { ...state.tableState, page },
      })),
    setLimit: (limit: number) =>
      set(state => ({
        tableState: { ...state.tableState, limit, page: 1 },
      })),
    setTotalItems: (totalItems: number) =>
      set(state => ({
        tableState: { ...state.tableState, totalItems },
      })),
  },

  // Actions
  setLoadingList: loadingList => set({ loadingList }),
  setLoadingAction: loadingAction => set({ loadingAction }),
  setStatus: status => set({ status }),
  setErrorMessage: errorMessage => set({ errorMessage }),
  setFeedback: feedback => set({ feedback }),
  setFilter: filter => set({ filter }),

  // Modal actions
  setOpenAddCrudModal: open => set({ openAddCrudModal: open }),
  setOpenEditCrudModal: (open, item) =>
    set({
      openEditCrudModal: open,
      selectedItem: item !== undefined ? item : null,
    }),
  setOpenDeleteCrudModal: (open, item) =>
    set({
      openDeleteCrudModal: open,
      selectedItem: item !== undefined ? item : null,
    }),
  setOpenViewCrudModal: (open, item) =>
    set({
      openViewCrudModal: open,
      selectedItem: item !== undefined ? item : null,
    }),
  setSelectedItem: item => set({ selectedItem: item }),

  // Close all modals
  closeCrudModal: () =>
    set({
      openAddCrudModal: false,
      openEditCrudModal: false,
      openDeleteCrudModal: false,
      openViewCrudModal: false,
      selectedItem: null,
    }),

  // Reset para estado inicial
  resetCrud: () =>
    set(state => ({
      ...initialState,
      tableState: {
        ...state.tableState,
        page: 1,
        limit: 10,
        totalItems: 0,
      },
    })),
}));

/**
 * Hook conveniente que retorna a mesma API do antigo CrudContext.useCrud()
 * Facilita a migração mantendo compatibilidade
 */
export const useCrud = () => {
  const store = useCrudStore();

  return {
    // Loading and status
    loadingList: store.loadingList,
    loadingAction: store.loadingAction,
    status: store.status,
    errorMessage: store.errorMessage,
    feedback: store.feedback,
    setLoadingList: store.setLoadingList,
    setLoadingAction: store.setLoadingAction,
    setStatus: store.setStatus,
    setErrorMessage: store.setErrorMessage,
    setFeedback: store.setFeedback,

    // Filter
    filter: store.filter,
    setFilter: store.setFilter,

    // Modals
    openAddCrudModal: store.openAddCrudModal,
    openEditCrudModal: store.openEditCrudModal,
    openDeleteCrudModal: store.openDeleteCrudModal,
    openViewCrudModal: store.openViewCrudModal,
    selectedItem: store.selectedItem,
    setOpenAddCrudModal: store.setOpenAddCrudModal,
    setOpenEditCrudModal: store.setOpenEditCrudModal,
    setOpenDeleteCrudModal: store.setOpenDeleteCrudModal,
    setOpenViewCrudModal: store.setOpenViewCrudModal,
    setSelectedItem: store.setSelectedItem,
    closeCrudModal: store.closeCrudModal,

    // Table
    tableState: store.tableState,

    // Reset
    resetCrud: store.resetCrud,
  };
};
